// MaisVendidos — pagina /cotacoes/mais-vendidos (Cotacoes > Mais Vendidos, 28/10/2026).
// Os 100 produtos que sairam em MAIS VENDAS nos ultimos 30 dias, pra compor cotacao. A regua
// (numero de vendas, nao quantidade nem faturamento) e a conta moram em giro-30d.ts no backend.
// Cada linha manda o produto pra uma cotacao aberta pelo MESMO modal da Lista de Compras
// (ModalSelecionarCotacao) — uma porta so pra cotacao.
//
// A tabela e' um componente (TabelaMaisVendidos) porque tem DOIS usos com as mesmas colunas: esta
// pagina e a janela do botao "N vendas · 30d" da lista de Produtos (ModalVendas30d, pedido do Ruan:
// "todas as colunas do Mais Vendidos"). Coluna nova entra aqui e aparece nos dois.
import { useCallback, useEffect, useState, type CSSProperties, type HTMLAttributes } from 'react';
import { ImageOff, Send, TrendingUp } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { FotoProduto } from '../../components/FotoProduto';
import { Modal } from '../../components/Modal';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { SeloChegando } from '../../components/SeloChegando';
import { EditorDataCompra, type AlvoEdicaoData } from '../../components/EditorDataCompra';
import { ModalSelecionarCotacao } from './ListaCompras';

export interface LinhaMaisVendidos {
  id: number;
  codigo_interno: string;
  descricao: string;
  foto_url: string | null;
  status: string;
  unidade_sigla: string | null;
  // Posicao no ranking de TODOS os vendidos na janela; null = nenhuma venda nos 30 dias.
  posicao: string | null;
  vendas: string;
  qtd_vendida: string;
  estoque_atual: string;
  estoque_minimo: string | null;
  compra_em_aberto: boolean;
  compra_em_aberto_qtd: string | null;
  compra_em_aberto_emb: string | null;
  ultima_compra_em: string | null;
  ultima_compra_fornecedor_nome: string | null;
  cotacoes_abertas: string[] | null;
}

const alertaMinimo: CSSProperties = { backgroundColor: '#ef4444', color: '#000' };

const fmtQtd = (v: string | null) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

interface TabelaProps {
  linhas: LinhaMaisVendidos[];
  onEnviar: (p: LinhaMaisVendidos) => void;
  // Estilo extra do cabecalho (a pagina passa o sticky; a janela nao).
  thExtra?: CSSProperties;
  tableStyle?: CSSProperties;
  // Grid da pagina (clique marca, setas). A janela, de uma linha so, nao usa.
  rowProps?: (p: LinhaMaisVendidos) => HTMLAttributes<HTMLTableRowElement>;
  // Clique no selo "Chegando" abre o ajuste da data (so a pagina; na janela o selo e' so leitura —
  // ela abre de cima da lista de Produtos, que ja tem o selo clicavel na mesma linha).
  onEditarData?: (p: LinhaMaisVendidos, rect: DOMRect) => void;
}

export function TabelaMaisVendidos({ linhas, onEnviar, thExtra, tableStyle, rowProps, onEditarData }: TabelaProps) {
  const th = (extra: CSSProperties = {}): CSSProperties => ({
    ...thExtra,
    padding: '8px 12px',
    fontWeight: 600,
    ...extra,
  });

  return (
    <table style={{ width: '100%', fontSize: 13, ...tableStyle }}>
      <thead>
        <tr>
          <th style={th({ textAlign: 'center', width: 48 })} title="Posicao no ranking de vendas dos ultimos 30 dias">#</th>
          <th style={th({ textAlign: 'center', width: 56, padding: '8px' })}>Foto</th>
          <th style={th({ textAlign: 'left', width: 64 })}>Cod.</th>
          <th style={th({ textAlign: 'left' })}>Descricao</th>
          <th style={th({ textAlign: 'right', width: 80 })} title="Em quantas vendas o produto saiu nos ultimos 30 dias">Vendas</th>
          <th style={th({ textAlign: 'right', width: 96 })}>Qtd vendida</th>
          <th style={th({ textAlign: 'center', width: 56 })}>Un.</th>
          <th style={th({ textAlign: 'right', width: 80 })}>Estoque</th>
          <th style={th({ textAlign: 'right', width: 88 })} title="Abaixo disso o produto entra na Lista de Compras">Mín. Compra</th>
          <th style={th({ width: 168 })}></th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((p) => {
          const minimo = Number(p.estoque_minimo ?? 0);
          // Produto "Chegando" (compra em aberto) ja foi comprado: nao pinta de vermelho (pedido dele,
          // 29/10/2026) — o selo ao lado da descricao ja conta a historia.
          const estoqueAlerta = minimo > 0 && Number(p.estoque_atual) <= minimo && !p.compra_em_aberto;
          const inativo = p.status !== 'ativo';
          const rp = rowProps?.(p);
          return (
            <tr key={p.id} {...rp} style={{ borderBottom: '1px solid var(--border)', ...rp?.style }}>
              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: 'var(--page-fg)' }}>
                {p.posicao ?? '—'}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                {p.foto_url ? (
                  <FotoProduto url={p.foto_url} tam={48} style={{ margin: '0 auto' }} />
                ) : (
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      background: 'var(--grid-row-alt-bg)',
                      borderRadius: 4,
                      border: '1px solid var(--border)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ImageOff size={16} color="#d1d5db" />
                  </div>
                )}
              </td>
              <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--page-fg)' }}>{p.codigo_interno}</td>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ fontWeight: 500, color: 'var(--page-fg)' }}>
                  {p.descricao}
                  {inativo && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>(INATIVO)</span>
                  )}
                  {/* Selo "Chegando" a direita da descricao (pedido do Ruan, 29/10/2026). */}
                  {p.compra_em_aberto && (
                    <SeloChegando
                      dataISO={p.ultima_compra_em}
                      fornecedorNome={p.ultima_compra_fornecedor_nome}
                      qtd={p.compra_em_aberto_qtd}
                      emb={p.compra_em_aberto_emb}
                      onEditar={onEditarData ? (rect) => onEditarData(p, rect) : undefined}
                    />
                  )}
                </div>
                {p.cotacoes_abertas && p.cotacoes_abertas.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ja em cotacao:</span>
                    {p.cotacoes_abertas.map((num) => (
                      <span
                        key={num}
                        style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 3,
                          background: 'var(--primary)',
                          color: 'var(--accent-fg)',
                        }}
                      >
                        {num}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--page-fg)' }}>{fmtQtd(p.vendas)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--page-fg)' }}>{fmtQtd(p.qtd_vendida)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>{p.unidade_sigla || '—'}</td>
              {/* Estoque no minimo ou abaixo (e nao Chegando): as DUAS celulas em fundo vermelho + fonte
                  preta (pedido dele, 29/10/2026; par de alerta da casa, vale tambem na linha selecionada). */}
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, ...(estoqueAlerta ? alertaMinimo : { color: 'var(--page-fg)' }) }}>
                {fmtQtd(p.estoque_atual)}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', ...(estoqueAlerta ? alertaMinimo : { color: 'var(--text-muted)' }) }}>
                {minimo > 0 ? fmtQtd(p.estoque_minimo) : '—'}
              </td>
              <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                {inativo ? (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Produto inativo</span>
                ) : (
                  <button
                    type="button"
                    className="btn-secundario"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnviar(p);
                    }}
                    onDoubleClick={(e) => e.stopPropagation()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap' }}
                  >
                    <Send size={13} /> Enviar p/ Cotacao
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Janela do botao "N vendas · 30d" da lista de Produtos: a linha do relatorio daquele produto.
// O "Enviar p/ Cotacao" FECHA esta janela e quem abriu mostra o ModalSelecionarCotacao — o modal
// da Lista de Compras e' um overlay z 70 e ficaria atras desta (z 1000 do Modal da casa).
export function ModalVendas30d({
  produtoId,
  onFechar,
  onEnviar,
}: {
  produtoId: number;
  onFechar: () => void;
  onEnviar: (p: LinhaMaisVendidos) => void;
}) {
  const [linha, setLinha] = useState<LinhaMaisVendidos | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .get<LinhaMaisVendidos>(`/api/lista-compras/mais-vendidos/produto/${produtoId}`)
      .then((r) => vivo && setLinha(r))
      .catch((e: unknown) => vivo && setErro(e instanceof ApiError ? e.message : 'Erro ao carregar as vendas'));
    return () => {
      vivo = false;
    };
  }, [produtoId]);

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Vendas dos ultimos 30 dias"
      largura={1100}
      rodape={
        <button type="button" className="btn-secundario" onClick={onFechar}>
          Fechar
        </button>
      }
    >
      {erro ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>{erro}</div>
      ) : !linha ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (
        <TabelaMaisVendidos
          linhas={[linha]}
          onEnviar={onEnviar}
          thExtra={{ background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}
        />
      )}
    </Modal>
  );
}

export function MaisVendidos() {
  const toast = useToast();
  const sticky = useStickyHeader();
  const [linhas, setLinhas] = useState<LinhaMaisVendidos[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalCotacao, setModalCotacao] = useState<{ produto_id: number; descricao: string } | null>(null);
  // Ajuste da data pelo selo "Chegando" — mesma rota e mesma caixinha da lista de Produtos (DA-368).
  const [edicaoData, setEdicaoData] = useState<AlvoEdicaoData | null>(null);
  const [salvandoData, setSalvandoData] = useState(false);

  async function confirmarDataCompra(dataISO: string) {
    const alvo = edicaoData;
    if (!alvo) return;
    setSalvandoData(true);
    try {
      const r = await api.patch<{ ultima_compra_em: string | null }>(`/api/produtos/${alvo.produtoId}/ultima-compra`, { data: dataISO });
      setLinhas((prev) => prev.map((p) => (p.id === alvo.produtoId ? { ...p, ultima_compra_em: r.ultima_compra_em } : p)));
      setEdicaoData(null);
      toast.sucesso('Data da compra atualizada.');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar a data');
    } finally {
      setSalvandoData(false);
    }
  }

  function abrirEnviar(p: LinhaMaisVendidos) {
    if (p.status !== 'ativo') return;
    grid.setSelectedId(p.id);
    setModalCotacao({ produto_id: p.id, descricao: p.descricao });
  }

  // Grid padrao da casa (DA-108): clique marca, setas navegam, Enter/duplo-clique = Enviar p/ Cotacao
  // (so abre o modal de escolher a cotacao, nao grava). Teclado desligado com o modal aberto.
  const grid = useGridNavegavel<LinhaMaisVendidos>({
    itens: linhas,
    getId: (p) => p.id,
    onAtivar: abrirEnviar,
    ativo: !modalCotacao && !edicaoData,
  });

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setLinhas(await api.get<LinhaMaisVendidos[]>('/api/lista-compras/mais-vendidos'));
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar os mais vendidos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <div className="modulo-pagina">
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            <TrendingUp size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
            Mais Vendidos
          </h2>
        </div>
      </div>

      <div style={{ padding: '12px 16px 16px' }}>
        <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          Os {linhas.length} produtos que sairam em mais vendas nos ultimos 30 dias
        </div>

        <div style={{ background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
          ) : linhas.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma venda nos ultimos 30 dias</div>
          ) : (
            <TabelaMaisVendidos
              linhas={linhas}
              onEnviar={abrirEnviar}
              thExtra={sticky.thStyle}
              tableStyle={sticky.tableStyle}
              rowProps={grid.rowProps}
              onEditarData={(p, rect) =>
                setEdicaoData({
                  produtoId: p.id,
                  rect,
                  dataAtualISO: p.ultima_compra_em,
                  fornNome: p.ultima_compra_fornecedor_nome,
                })
              }
            />
          )}
        </div>
      </div>

      {edicaoData && (
        <EditorDataCompra
          alvo={edicaoData}
          salvando={salvandoData}
          onCancelar={() => setEdicaoData(null)}
          onConfirmar={(d) => void confirmarDataCompra(d)}
        />
      )}

      {modalCotacao && (
        <ModalSelecionarCotacao
          produtoId={modalCotacao.produto_id}
          produtoDesc={modalCotacao.descricao}
          onClose={(enviou) => {
            setModalCotacao(null);
            if (enviou) carregar();
          }}
        />
      )}
    </div>
  );
}
